import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Stack,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import { streamButtonState, isStreamButtonBusy } from './streamButtonState';
import { countingStartErrorKey } from './countingStartError';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
//import CancelIcon from '@mui/icons-material/Cancel';
import { IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import cerdodelosllanosCover from '../assets/images/cerdodelosllanos_cover.jpeg';
import { useTranslation } from 'react-i18next';
import { formatArgentineDate } from '../utils/dateUtils';
import authorizedFetch from '../api/authorizedFetch';
import { useScreen } from '../hooks/useScreen';
import { useGridHeight } from '../hooks/useGridHeight';

const PigCounter = () => {
  const { t } = useTranslation();
  const { isXs, isMdUp } = useScreen();
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = Boolean(user?.is_admin);
  const [camera, setCamera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCounting, setIsCounting] = useState(false);
  const [isCountingActive, setIsCountingActive] = useState(false);
  const [pigCount, setPigCount] = useState(null);
  const [videoKey, setVideoKey] = useState(Date.now());
  const [isStreamLoading, setIsStreamLoading] = useState(false);
  const [streamError, setStreamError] = useState('');
  const [isStoppingStream, setIsStoppingStream] = useState(false);
  const streamTimeoutRef = useRef(null);
  const countingTimerRef = useRef(null);
  const countingStartTimeRef = useRef(null);
  const stoppingCountRef = useRef(false);
  const videoCheckIntervalRef = useRef(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isStoppingCount, setIsStoppingCount] = useState(false);
  const [registries, setRegistries] = useState([]);
  const [processingMap, setProcessingMap] = useState({});
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [editingMetadataRegistry, setEditingMetadataRegistry] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [formData, setFormData] = useState({
    supervised_count: '',
    exit_room: '',
    destination_room: '',
    lote_number: '',
    trip_number: '',
    pig_sex: ''
  });
  const AUTO_STOP_MS = 10 * 60 * 1000;      // 5 minutes
  const CONFIRM_WINDOW_MS = 20 * 1000;     // 20 seconds
  const autoStopTimeoutRef = useRef(null);
  const confirmTimeoutRef = useRef(null);
  const confirmCountdownIntervalRef = useRef(null);
  const [showAutoStopDialog, setShowAutoStopDialog] = useState(false);
  const [autoStopCountdown, setAutoStopCountdown] = useState(CONFIRM_WINDOW_MS / 1000);
  const [uploadingMap, setUploadingMap] = useState({}); // registryId -> bool
  const [deletingMap, setDeletingMap] = useState({}); // registryId -> bool
  const fileInputRef = useRef(null);
  const [uploadTargetRegistry, setUploadTargetRegistry] = useState(null);


  // Which of the four looks the live-stream button wears right now.
  const streamBtn = streamButtonState({ isStreaming, isStreamLoading, isStoppingStream });

  const historyHeight = useGridHeight(registries.length, {
    row: isXs ? 44 : 52,
    header: 56,
    footer: 52,
    max: isXs ? 420 : 640
  });

  const showAlert = React.useCallback((message, severity = 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setTimeout(() => setAlertMessage(''), 5000);
  }, []);

  const clearStreamTimeout = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  const armStreamTimeout = useCallback(() => {
    clearStreamTimeout();
    streamTimeoutRef.current = setTimeout(() => {
      setIsStreamLoading(false);
      setStreamError(t('stream_connection_lost') || 'La transmisión no respondió a tiempo. Probá reconectar.');
    }, 15000);
  }, [clearStreamTimeout, t]);

  const clearAutoStopTimers = useCallback(() => {
    if (autoStopTimeoutRef.current) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }
    if (confirmTimeoutRef.current) {
      clearTimeout(confirmTimeoutRef.current);
      confirmTimeoutRef.current = null;
    }
    if (confirmCountdownIntervalRef.current) {
      clearInterval(confirmCountdownIntervalRef.current);
      confirmCountdownIntervalRef.current = null;
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOpenModal = (e) => {
    e?.currentTarget?.blur();
    setEditingMetadataRegistry(null);
    setFormData({
      supervised_count: '',
      exit_room: '',
      destination_room: '',
      lote_number: '',
      trip_number: '',
      pig_sex: ''
    });
    setShowMetadataModal(true);
  };

  const handleCloseModal = () => {
    setShowMetadataModal(false);
    setEditingMetadataRegistry(null);
  };

  const handleOpenMetadataEdit = (row, e) => {
    e?.currentTarget?.blur();
    setEditingMetadataRegistry(row);
    setFormData({
      supervised_count: row.supervised_count ?? '',
      exit_room: row.exit_room ?? '',
      destination_room: row.destination_room ?? '',
      lote_number: row.lote_number ?? '',
      trip_number: row.trip_number ?? '',
      pig_sex: row.pig_sex ?? ''
    });
    setShowMetadataModal(true);
  };

  const handleDeleteRegistry = useCallback(async (row, e) => {
    e?.currentTarget?.blur();

    const confirmed = window.confirm(
      t('confirm_delete_registry') || 'Are you sure you want to delete this registry?'
    );
    if (!confirmed) return;

    setDeletingMap((prev) => ({ ...prev, [row.id]: true }));

    try {
      await api.delete(`/registries/${row.id}`);
      setRegistries((prev) => prev.filter((registry) => registry.id !== row.id));
      showAlert(t('registry_deleted_success') || 'Registry deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting registry:', error);
      showAlert(t('error_deleting_registry') || 'There was an error deleting the registry', 'error');
    } finally {
      setDeletingMap((prev) => {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      });
    }
  }, [showAlert, t]);

  const fetchRegistries = useCallback(async () => {
    try {
      const res = await api.get(`/registries/by-camera/${id}`);
      // Sort registries by start_date in descending order (newest first)
      const sortedRegistries = res.data.sort((a, b) => 
        new Date(b.start_date) - new Date(a.start_date)
      );
      setRegistries(sortedRegistries);
      return sortedRegistries;
    } catch (err) {
      console.error("Error fetching registries:", err);
      showAlert(t('error_fetching_registries'), 'error');
    }
  }, [id, t, showAlert]);

  // Check for counted_video in the latest registry every 30 seconds
  const startVideoCheck = useCallback(() => {
    // Clear any existing interval
    if (videoCheckIntervalRef.current) {
      clearInterval(videoCheckIntervalRef.current);
    }

    videoCheckIntervalRef.current = setInterval(async () => {
      try {
        const updatedRegistries = await fetchRegistries();
        
        if (updatedRegistries.length > 0) {
          const latestRegistry = updatedRegistries[0]; // First item (newest due to sorting)
          
          // Check if the latest registry has counted_video (video_link)
          if (latestRegistry.video_link) {
            // Counted video is ready, stop checking
            clearInterval(videoCheckIntervalRef.current);
            videoCheckIntervalRef.current = null;
            showAlert(t('counted_video_ready'), 'success');
            console.log('Counted video is ready for latest registry');
          } else {
            console.log('Still waiting for counted video in latest registry...');
          }
        }
      } catch (error) {
        console.error('Error checking video updates:', error);
      }
    }, 10000); // Check every 10 seconds
  }, [fetchRegistries, t, showAlert]);

  // Poll a single registry by id until video_link is present/changed
  const pollRegistryOnce = useCallback((registryId, { intervalMs = 5000, maxTries = 60 } = {}) => {
    let tries = 0;
    const h = setInterval(async () => {
      tries += 1;
      try {
        const updated = await fetchRegistries(); // reuse your fetch; it returns sorted registries
        const row = updated?.find(r => r.id === registryId);
        if (row && row.video_link) {
          showAlert(t('counted_video_ready'), 'success');
          clearInterval(h);
          setProcessingMap(prev => {
            const copy = { ...prev };
            delete copy[registryId];
            return copy;
          });
        } else if (tries >= maxTries) {
          clearInterval(h);
          showAlert(t('processing_taking_longer'), 'info');
          setProcessingMap(prev => {
            const copy = { ...prev };
            delete copy[registryId];
            return copy;
          });
        }
      } catch {}
    }, intervalMs);
  }, [fetchRegistries, t, showAlert]);

  // Stop video checking
  const stopVideoCheck = useCallback(() => {
    if (videoCheckIntervalRef.current) {
      clearInterval(videoCheckIntervalRef.current);
      videoCheckIntervalRef.current = null;
    }
  }, []);    

  useEffect(() => {
    let isMounted = true;

    const fetchCamera = async () => {
      try {
        const res = await api.get(`/cameras/${id}`);
        if (isMounted) {
          setCamera(res.data);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading camera:', err);
        setLoading(false);
        showAlert(t('error_loading_camera'), 'error');
      }
    };

    const stopStreamOnUnmount = async () => {
      try {
        await authorizedFetch(`${process.env.REACT_APP_API_URL}/video-stream/stop-stream/${id}`);
      } catch (error) {
        console.error('Error stopping stream:', error);
      }
      clearStreamTimeout();
      clearInterval(countingTimerRef.current);
      stopVideoCheck(); // Use the new function
      setIsStreaming(false);
      setIsStreamLoading(false);
      setStreamError('');
      setVideoKey(Date.now());
    };

    fetchCamera();
    fetchRegistries();
    // Don't start video check on component mount, only after counting

    return () => {
      isMounted = false;
      stopStreamOnUnmount();
      clearAutoStopTimers();
    };
  }, [id, fetchRegistries, t, stopVideoCheck, clearAutoStopTimers, showAlert, clearStreamTimeout]);

  const retryStream = useCallback(() => {
    if (!isStreaming) return;
    setStreamError('');
    setIsStreamLoading(true);
    setVideoKey(Date.now());
    armStreamTimeout();
    showAlert(t('reconnecting_stream') || 'Reconectando transmisión...', 'info');
  }, [armStreamTimeout, isStreaming, showAlert, t]);

  const startStream = async () => {
    setIsStreamLoading(true);
    setStreamError('');
    try {
      await authorizedFetch(`${process.env.REACT_APP_API_URL}/video-stream/start-stream/${id}`);
      setIsStreaming(true);
      setPigCount(null);
      setVideoKey(Date.now()); // Forces stream refresh
      armStreamTimeout();
      scheduleAutoStop();
      showAlert(t('stream_started_successfully'), 'success');
    } catch (error) {
      console.error('Error starting stream:', error);
      setIsStreamLoading(false);
      showAlert(t('error_starting_stream'), 'error');
    }
  };

  const stopCountingAndSave = useCallback(async () => {
    // A ref, not the state flag: two calls in the same tick would both read the old
    // state value and both fire the request.
    if (stoppingCountRef.current) return;
    stoppingCountRef.current = true;

    // Stop the session timer up front so it cannot fire again while the request is
    // still in flight.
    if (countingTimerRef.current) {
      clearInterval(countingTimerRef.current);
      countingTimerRef.current = null;
    }

    setIsStoppingCount(true);
    try {
      await authorizedFetch(`${process.env.REACT_APP_API_URL}/video-stream/stop-counting/${id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // The count is saved the moment the backend answers. Release the button here:
      // refreshing the history below is a second request, and making the operator
      // watch it was half of the wait they were looking at.
      setIsCountingActive(false);
      setIsStoppingCount(false);
      setElapsedSeconds(0);
      showAlert(t('count_stopped_and_saved'), 'success');

      // Refresh the history without holding the caller. stopStream awaits this
      // function before it can send its own request, and the stream button stays
      // disabled for that whole wait - so awaiting a refresh nobody needs here is
      // what left the operator pressing a dead button after the count was saved.
      //
      // fetchRegistries reports its own failure and returns nothing, hence the
      // guard: reading .length off that used to throw a saved count into the catch
      // below and report it as a failed stop.
      fetchRegistries().then((rows) => {
        const updatedRegistries = rows || [];
        if (updatedRegistries.length === 0) return;

        const latestRegistry = updatedRegistries[0]; // Now sorted by date desc
        setPigCount(latestRegistry.count);

        // Start video checking specifically for the counted_video of the latest registry
        if (!latestRegistry.video_link) {
          showAlert(t('waiting_for_counted_video'), 'info');
          startVideoCheck();
        }
      });
    } catch (error) {
      console.error('Error stopping and saving count:', error);
      showAlert(t('error_stopping_count'), 'error');
    } finally {
      setIsCountingActive(false);
      setIsStoppingCount(false);
      setElapsedSeconds(0);
      stoppingCountRef.current = false;
    }
  }, [id, fetchRegistries, t, showAlert, startVideoCheck]);

  const stopStream = useCallback(async () => {
    // If counting is active, stop counting first
    setIsStoppingStream(true);
    setShowAutoStopDialog(false);
    clearAutoStopTimers();
    if (isCountingActive) {
      showAlert(t('stopping_count_before_stream'), 'warning');
      await stopCountingAndSave();
    }

    try {
      await authorizedFetch(`${process.env.REACT_APP_API_URL}/video-stream/stop-stream/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      showAlert(t('stream_stopped_successfully'), 'info');
    } catch (error) {
      console.error('Error stopping stream:', error);
      showAlert(t('error_stopping_stream'), 'error');
    }
    clearStreamTimeout();
    clearInterval(countingTimerRef.current);
    setIsStreaming(false);
    setIsStreamLoading(false);
    setStreamError('');
    setVideoKey(Date.now());
    setIsStoppingStream(false);
  }, [id, isCountingActive, stopCountingAndSave, showAlert, clearAutoStopTimers, t, clearStreamTimeout]);

  // When 5 minutes elapse, show a confirm dialog for 20s. If no response, stop.
  const scheduleAutoStop = useCallback(() => {
    clearAutoStopTimers();
    autoStopTimeoutRef.current = setTimeout(() => {
      setShowAutoStopDialog(true);
      setAutoStopCountdown(CONFIRM_WINDOW_MS / 1000);
      // Start visible countdown
      confirmCountdownIntervalRef.current = setInterval(() => {
        setAutoStopCountdown((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      // If no response in 20s, stop automatically
      confirmTimeoutRef.current = setTimeout(() => {
        setShowAutoStopDialog(false);
        stopStream();
      }, CONFIRM_WINDOW_MS);
    }, AUTO_STOP_MS);
  }, [clearAutoStopTimers, stopStream, AUTO_STOP_MS, CONFIRM_WINDOW_MS]);

  const handleContinueStreaming = () => {
    // User wants to keep streaming: hide dialog, clear confirm timers, schedule next 5 minutes
    setShowAutoStopDialog(false);
    if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
    if (confirmCountdownIntervalRef.current) clearInterval(confirmCountdownIntervalRef.current);
    showAlert(t('continuing_streaming') || 'Continuing streaming', 'info');
    scheduleAutoStop();
  };
  
  const handleStopStreamingNow = () => {
    // User explicitly chose to stop
    setShowAutoStopDialog(false);
    if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
    if (confirmCountdownIntervalRef.current) clearInterval(confirmCountdownIntervalRef.current);
    stopStream();
  };  

  const handleStartCounting = async () => {
    // Guard against a second start while one is already open or in flight.
    if (isCounting || isCountingActive) return;
    setIsCounting(true);

    // Stop any existing video check before starting new counting
    stopVideoCheck();
    // Drop a timer from a previous session before opening a new one.
    if (countingTimerRef.current) {
      clearInterval(countingTimerRef.current);
      countingTimerRef.current = null;
    }

    try {
      // First, send metadata
      await api.post(`/video-stream/set-metadata/${id}`, formData);

      // Then, start counting
      const response = await authorizedFetch(`${process.env.REACT_APP_API_URL}/video-stream/start-counting/${id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Carry the status out with the failure: it is what separates a camera that
      // is already counting from a refused form or an expired session.
      if (!response.ok) {
        const failure = new Error("Error during pig counting");
        failure.status = response.status;
        throw failure;
      }

      const data = await response.json();
      setPigCount(data.count);
      setIsCountingActive(true);
      setShowMetadataModal(false);
      countingStartTimeRef.current = Date.now();
      setElapsedSeconds(0);
      showAlert(t('counting_started_successfully'), 'success');

      // Hold this interval's own id. Clearing countingTimerRef.current from inside the
      // callback would clear whatever timer the ref points at *now* — so a timer left
      // over from an earlier session would survive and keep firing the auto-stop.
      const timerId = setInterval(() => {
        const secondsElapsed = Math.floor((Date.now() - countingStartTimeRef.current) / 1000);
        setElapsedSeconds(secondsElapsed);

        if (secondsElapsed >= 180) {
          clearInterval(timerId);
          if (countingTimerRef.current === timerId) countingTimerRef.current = null;
          stopCountingAndSave();
        }
      }, 1000);
      countingTimerRef.current = timerId;
    } catch (error) {
      console.error("Error starting count with metadata:", error);
      // axios reports the status on the response, our fetch wrapper on the error.
      showAlert(t(countingStartErrorKey(error?.status ?? error?.response?.status)), 'error');
    } finally {
      setIsCounting(false);
    }
  };

  const handleProcessRegistry = async (row, e) => {
    e?.currentTarget?.blur();
    try {
      setProcessingMap(prev => ({ ...prev, [row.id]: true }));
      const res = await authorizedFetch(`${process.env.REACT_APP_API_URL}/video-stream/process-registry/${row.id}`, {
        method: 'POST',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || `HTTP ${res.status}`);
      }
      showAlert(t('processing_started'), 'info');
      // Start polling just this row
      pollRegistryOnce(row.id);
    } catch (err) {
      console.error('Error starting processing:', err);
      showAlert(t('error_starting_processing'), 'error');
      setProcessingMap(prev => {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      });
    }
  };
  
  const handleOpenUpload = (row, e) => {
    e?.currentTarget?.blur();
    setUploadTargetRegistry(row);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !uploadTargetRegistry) {
      event.target.value = '';
      return;
    }

    const registryId = uploadTargetRegistry.id;
    setUploadingMap((prev) => ({ ...prev, [registryId]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);   // <- MUST be called "file"

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/video-stream/upload-raw/${registryId}`,
        {
          method: 'POST',
          headers: {
            // DO NOT set "Content-Type", browser will set multipart boundary
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        }
      );

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || `HTTP ${res.status}`);
      }

      showAlert(t('video_uploaded_success') || 'Video uploaded successfully', 'success');
      await fetchRegistries();
    } catch (err) {
      console.error('Error uploading raw video:', err);
      showAlert(t('error_uploading_video') || 'Error uploading video', 'error');
    } finally {
      setUploadingMap((prev) => {
        const copy = { ...prev };
        delete copy[registryId];
        return copy;
      });
      setUploadTargetRegistry(null);
      event.target.value = '';
    }
  };

  const handleUpdateRegistryMetadata = async () => {
    if (!editingMetadataRegistry) return;

    try {
      await api.put(`/registries/${editingMetadataRegistry.id}/metadata`, formData);
      showAlert(t('metadata_updated_successfully') || 'Metadata updated successfully', 'success');
      await fetchRegistries();
      handleCloseModal();
    } catch (error) {
      console.error('Error updating registry metadata:', error);
      showAlert(t('error_updating_metadata') || 'Error updating metadata', 'error');
    }
  };

  // DataGrid columns configuration
  const columns = [
    {
      field: 'start_date',
      headerName: t('start'),
      width: 150,
      renderCell: (params) => formatArgentineDate(params.value),
    },
    {
      field: 'end_date',
      headerName: t('end'),
      width: 150,
      renderCell: (params) => formatArgentineDate(params.value),
    },
    // {
    //   field: 'elapsed_time',
    //   headerName: t('elapsed_time'),
    //   width: 120,
    //   renderCell: (params) => {
    //     const elapsedMinutes = Math.round(
    //       (new Date(params.row.end_date) - new Date(params.row.start_date)) / 60000
    //     );
    //     return `${elapsedMinutes} min`;
    //   },
    // },
    {
      field: 'lote_number',
      headerName: t('lote_number'),
      width: 120,
    },
    {
      field: 'exit_room',
      headerName: t('exit_room'),
      width: 120,
    },
    // {
    //   field: 'destination_room',
    //   headerName: t('destination_room'),
    //   width: 150,
    // },
    {
      field: 'trip_number',
      headerName: t('trip_number'),
      width: 120,
    },
    {
      field: 'pig_sex',
      headerName: t('pig_sex'),
      width: 120,
      renderCell: (params) => {
        if (params.value === 'CAP') return t('CAP');
        if (params.value === 'MEI') return t('MEI');
        if (params.value === 'CACH') return t('CACH');
        return '-';
      }
    },
    {
      field: 'supervised_count',
      headerName: t('supervised_count'),
      width: 140,
    },
    {
      field: 'count',
      headerName: t('count'),
      width: 100,
      renderCell: (params) => 
        params.value !== null ? params.value : <CircularProgress size={20} color="secondary" />
    },
    {
      field: 'raw_video_link',
      headerName: t('raw_video'),
      width: 120,
      renderCell: (params) => 
        params.value ? (
          <a
            href={`${process.env.REACT_APP_API_URL}/registries/download/${params.value}`}
            download
            style={{ textDecoration: 'none', color: '#9c27b0' }}
          >
            {t('download')}
          </a>
        ) : (
          <CircularProgress size={20} color="inherit" />
        )
    },
    {
      field: 'video_link',
      headerName: t('counted_video'),
      width: 120,
      renderCell: (params) => 
        params.value ? (
          <a
            href={`${process.env.REACT_APP_API_URL}/registries/download/${params.value}`}
            download
            style={{ textDecoration: 'none', color: '#1976d2' }}
          >
            {t('download')}
          </a>
        ) : (
          <CircularProgress size={20} color="primary" />
        )
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Actions',
      minWidth: 220,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const isProcessing = !!processingMap[row.id];
        const isUploading = !!uploadingMap[row.id];
        const isDeleting = !!deletingMap[row.id];
        const canProcess = !!row.raw_video_link;
        const actionsDisabled = isProcessing || isUploading || isDeleting;
        const isReprocess = !!row.video_link;

        const onProcessClick = (e) => {
          e?.currentTarget?.blur();
          if (isProcessing || !canProcess) return;

          if (isReprocess) {
            const msg =
              t('confirm_reprocess') ||
              'This will overwrite the existing processed video. Continue?';
            if (!window.confirm(msg)) return;
          }
          handleProcessRegistry(row, e);
        };

        const onUploadClick = (e) => {
          if (actionsDisabled) return;
          handleOpenUpload(row, e);
        };

        const onEditMetadataClick = (e) => {
          if (actionsDisabled) return;
          handleOpenMetadataEdit(row, e);
        };

        const onDeleteClick = (e) => {
          if (actionsDisabled) return;
          handleDeleteRegistry(row, e);
        };

        return (
          <Stack direction="row" spacing={1}>

            {isAdmin && (
              <Tooltip title={t('edit') || 'Edit'}>
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={onEditMetadataClick}
                    disabled={actionsDisabled}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* UPLOAD ICON */}
            {isAdmin && (
              <Tooltip title={t('upload_video') || 'Upload video'}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={onUploadClick}
                    disabled={actionsDisabled}
                  >
                    {isUploading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <UploadIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* PROCESS / REPROCESS ICON */}
            <Tooltip title={
              canProcess
                ? (isReprocess
                    ? t('reprocess') || 'Reprocess'
                    : t('process') || 'Process'
                  )
                : t('no_raw') || 'No raw video'
            }>
              <span>
                <IconButton
                  size="small"
                  color={isReprocess ? 'error' : 'success'}
                  onClick={onProcessClick}
                  disabled={actionsDisabled || !canProcess}
                >
                  {isProcessing ? (
                    <CircularProgress size={18} />
                  ) : isReprocess ? (
                    <ReplayIcon />
                  ) : (
                    <PlayCircleIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {isAdmin && (
              <Tooltip title={t('delete') || 'Delete'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={onDeleteClick}
                    disabled={actionsDisabled}
                  >
                    {isDeleting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

          </Stack>
        );
      }
    }
  ];

  if (loading) {
    return (
      <Container sx={{ textAlign: 'center', mt: 8 }}>
        <CircularProgress />
        <Typography>{t('loading_camera')}</Typography>
      </Container>
    );
  }

  if (!camera) {
    return (
      <Container sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h5" color="error">{t('camera_not_found')}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters={isXs} sx={{ px: isXs ? 1 : 2 }}>
      {/* Alert Messages */}
      {alertMessage && (
        <Alert severity={alertSeverity} sx={{ mb: 2 }}>
          {alertMessage}
        </Alert>
      )}

      <Box textAlign="center" my={4}>
        <Typography variant="h4" gutterBottom>
          {t('pig_counter')} – {camera.location}
        </Typography>

        <Typography variant="subtitle1" gutterBottom color="text.secondary">
          {t('nave')}: {camera.nave} | {t('notes')}: {camera.notes}
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardMedia
            component="img"
            image={cerdodelosllanosCover}
            alt="Cerdo de los Llanos Logo"
            sx={{ maxHeight: { xs: 180, md: 300 }, objectFit: 'cover' }}
          />
        </Card>
        <Box sx={{ my: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="center">
            <Button
              fullWidth={isXs}
              variant="contained"
              color={streamBtn === 'live' ? 'error' : 'primary'}
              onClick={isStreaming ? stopStream : startStream}
              disabled={isStreamButtonBusy(streamBtn)}
              startIcon={
                streamBtn === 'connecting' || streamBtn === 'stopping' ? (
                  <CircularProgress size={18} color="inherit" />
                ) : streamBtn === 'live' ? (
                  <StopCircleIcon />
                ) : (
                  <PlayCircleIcon />
                )
              }
              sx={{
                // Hold one width across all four labels so the row never jumps
                // as the text changes under the operator's finger.
                minWidth: isXs ? undefined : 260,
                transition: 'background-color .25s ease, box-shadow .25s ease',
                '& .MuiButton-startIcon': { transition: 'transform .25s ease' },
                '&:not(:disabled):hover .MuiButton-startIcon': { transform: 'scale(1.12)' },
                // While live, breathe the shadow so a running stream is visible
                // from across the room, not only by reading the label.
                ...(streamBtn === 'live' && {
                  animation: 'streamLive 2s ease-in-out infinite',
                  '@keyframes streamLive': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(211,47,47,.45)' },
                    '50%': { boxShadow: '0 0 0 10px rgba(211,47,47,0)' },
                  },
                }),
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                  transition: 'none',
                  '& .MuiButton-startIcon': { transition: 'none' },
                },
              }}
            >
              {streamBtn === 'connecting'
                ? t('connecting_stream_button')
                : streamBtn === 'stopping'
                ? t('stopping_stream_button')
                : streamBtn === 'live'
                ? t('stop_live_stream')
                : t('start_live_stream')}
            </Button>
          
            {!isCountingActive ? (
              <Button
                fullWidth={isXs}
                variant="contained"
                color="secondary"
                onClick={handleOpenModal}
                disabled={isStreaming ? isCounting : true}
              >
                {isCounting ? <CircularProgress size={24} /> : t('start_counting_pigs')}
              </Button>
            ) : (
              <Button
                fullWidth={isXs}
                variant="contained"
                color="warning"
                onClick={stopCountingAndSave}
                disabled={isStoppingCount}
              >
                {isStoppingCount ? <CircularProgress size={24} /> : t('stop_and_save_count')}
              </Button>
            )}
          </Stack>
          {isCountingActive && (
            <Typography align="center" sx={{ mt: 1 }}>
              ⏱️ {t('elapsed_time')}: {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
            </Typography>
          )}
        </Box>
      </Box>

      <Card>
        {(isStreaming || isCounting) && (
          <>
            {isStreamLoading && (
              <Box textAlign="center" my={2}>
                <CircularProgress />
                <Typography>{t('loading_stream')}</Typography>
              </Box>
            )}
            {streamError && (
              <Box textAlign="center" my={2} px={2}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {streamError}
                </Alert>
                <Button variant="outlined" startIcon={<ReplayIcon />} onClick={retryStream}>
                  Reintentar transmisión
                </Button>
              </Box>
            )}
            {/* <img
              src={`${process.env.REACT_APP_API_URL}/video-stream/stream/${id}?${videoKey}`}
              alt="Live Pig Stream"
              style={{ width: '100%', maxHeight: 500 }}
              onLoad={() => setIsStreamLoading(false)}
            /> */}
            <img
              src={`${process.env.REACT_APP_API_URL}/video-stream/stream/${id}?${videoKey}`}
              alt="Live Pig Stream"
              style={{ width: '100%', maxHeight: isMdUp ? 500 : 240, objectFit: 'contain', display: streamError ? 'none' : 'block' }}
              onLoad={() => {
                clearStreamTimeout();
                setIsStreamLoading(false);
                setStreamError('');
              }}
              onError={() => {
                clearStreamTimeout();
                setIsStreamLoading(false);
                setStreamError(t('stream_connection_lost') || 'La transmisión se interrumpió. Probá reconectar.');
              }}
            />
          </>
        )}
        <CardContent>
          {pigCount !== null ? (
            <Typography variant="h5" align="center">
              🐷 {t('detected_pig_count')}: {pigCount}
            </Typography>
          ) : (
            <Typography variant="body1" align="center">
              {isStreaming ? t('live_stream_active') : t('start_live_stream_hint')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Metadata Modal */}
      <Dialog open={showMetadataModal} onClose={handleCloseModal} maxWidth="sm" fullWidth fullScreen={isXs}>
        <DialogTitle>{editingMetadataRegistry ? (t('edit') || 'Editar') : t('enter_metadata')}</DialogTitle>
        <DialogContent>
          {/* The dialog is a portal above the page, so the alert at the top of the
              container is painted behind it - and behind the whole screen on a
              phone, where the dialog is fullScreen. A start that fails has to say
              so where the operator is actually looking. */}
          {alertMessage && (
            <Alert severity={alertSeverity} sx={{ mb: 2 }}>
              {alertMessage}
            </Alert>
          )}
          <TextField
            label={t('supervised_count')}
            name="supervised_count"
            value={formData.supervised_count}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
            type="number"
          />
          <TextField
            label={t('exit_room')}
            name="exit_room"
            value={formData.exit_room}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            sx={{ display: 'none' }}
            label={t('destination_room')}
            name="destination_room"
            value={formData.destination_room}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            //required
          />
          {/* <TextField
            label={t('lote_number')}
            name="lote_number"
            value={formData.lote_number}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          /> */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('lote_number')}</InputLabel>
            <Select
              name="lote_number"
              value={formData.lote_number}
              label={t('lote_number')}
              onChange={handleInputChange}
            >
              <MenuItem value="R1A">{'R1A'}</MenuItem>
              <MenuItem value="R1B">{'R1B'}</MenuItem>
              <MenuItem value="R2A">{'R2A'}</MenuItem>
              <MenuItem value="R2B">{'R2B'}</MenuItem>
              <MenuItem value="R3A">{'R3A'}</MenuItem>
              <MenuItem value="R3B">{'R3B'}</MenuItem>
              <MenuItem value="R4A">{'R4A'}</MenuItem>
              <MenuItem value="R4B">{'R4B'}</MenuItem>
              <MenuItem value="R5A">{'R5A'}</MenuItem>
              <MenuItem value="R5B">{'R5B'}</MenuItem>
              <MenuItem value="R6A">{'R6A'}</MenuItem>
              <MenuItem value="R6B">{'R6B'}</MenuItem>
              <MenuItem value="R7A">{'R7A'}</MenuItem>
              <MenuItem value="R7B">{'R7B'}</MenuItem>
              <MenuItem value="R8A">{'R8A'}</MenuItem>
              <MenuItem value="R8B">{'R8B'}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('trip_number')}
            name="trip_number"
            value={formData.trip_number}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('pig_sex')}</InputLabel>
            <Select
              name="pig_sex"
              value={formData.pig_sex}
              label={t('pig_sex')}
              onChange={handleInputChange}
            >
              <MenuItem value="CAP">{t('CAP')}</MenuItem>
              <MenuItem value="MEI">{t('MEI')}</MenuItem>
              <MenuItem value="CACH">{t('CACH')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {t('cancel')}
          </Button>
          <Button
            onClick={editingMetadataRegistry ? handleUpdateRegistryMetadata : handleStartCounting}
            variant="contained"
            color="primary"
            disabled={
              !formData.exit_room || 
              //!formData.destination_room || 
              !formData.lote_number || 
              !formData.trip_number ||
              !formData.pig_sex ||
              !formData.supervised_count ||
              (!editingMetadataRegistry && isCounting)
            }
          >
            {editingMetadataRegistry ? (t('save') || 'Guardar') : (isCounting ? <CircularProgress size={24} /> : t('start_counting'))}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Auto-stop confirmation (opens after 5 minutes) */}
      <Dialog
        open={showAutoStopDialog}
        onClose={(e, reason) => {
          // Don’t allow closing by backdrop/escape—force a choice or timeout
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setShowAutoStopDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('stream_will_stop_soon') || 'The live stream will stop soon'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('stream_will_stop_in_seconds', { seconds: autoStopCountdown }) ||
              `The live stream will stop in ${autoStopCountdown}s unless you choose to continue.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStopStreamingNow} color="error" variant="outlined">
            {t('stop_now') || 'Stop now'}
          </Button>
          <Button onClick={handleContinueStreaming} color="primary" variant="contained" autoFocus>
            {t('continue_streaming') || 'Continue streaming'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden input for per-registry video upload */}
      <input
        type="file"
        accept="video/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />  

      {/* Registry History with DataGrid */}
      <Box mt={5}>
        <Typography variant="h5" gutterBottom>
          {t('registry_history')}
        </Typography>
        {registries.length === 0 ? (
          <Typography>{t('no_records_found')}</Typography>
        ) : (
          <Box sx={{ width: '100%', height: historyHeight }}>
            <DataGrid
              rows={registries}
              columns={columns}
              density={isXs ? 'compact' : 'standard'}
              initialState={{
                pagination: { paginationModel: { pageSize: isXs ? 5 : 10 } },
                columns: {
                  columnVisibilityModel: {
                    //raw_video_link: !isXs, // hide both videos on phones if needed
                    //video_link: !isXs,
                    trip_number: !isXs
                  }
                }
              }}
              pageSizeOptions={[5, 10, 25]}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default PigCounter;
